/**
 * A collapsed section for the figures most people will not fill in.
 *
 * Four forms need this — the manual entry, the food editor, the correction
 * panel and the scan sheet's fix form — and four hand-rolled disclosures is
 * how those four drift. `FoodEditor` records that these forms have already
 * been through one round of exactly that.
 *
 * Fibre, sugar, salt and saturated fat belong behind it rather than beside the
 * macros: someone entering a food from a packet usually wants the four
 * numbers, and four more boxes on the way to saving turns a fast form into a
 * data-entry chore. Behind one line they cost nothing to ignore.
 *
 * Whether it starts open is the caller's decision, and it carries the weight:
 * hiding a value that already exists is worse than the extra height, while
 * hiding four empty boxes is the entire point.
 */

import { useId, useState, type ReactNode } from "react";

export function OptionalDetail({
  label = "Optional detail",
  defaultOpen = false,
  children,
}: {
  label?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();

  return (
    <div className="optional">
      {/* A button with aria-expanded rather than <details>, which this app uses
          exactly once. Same idiom as the meal heads and the day's More row. */}
      <button
        type="button"
        className="optional__head"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
      </button>
      {open && (
        <div className="optional__body stack" id={id}>
          {children}
        </div>
      )}
    </div>
  );
}
